import { useCallback } from "react";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { completeLeadTimeConfiguration } from "src/core/orders/utils/order-deliveries";
import { useDrop } from "src/core/drops/hooks";
import { WeekDay } from "src/core/drops/api/drops.api";
import { IProductLeadTimeGroup } from "src/core/products/api/products.api";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

/**
 * Returns the cut-off time to deliver an order.
 *
 * @param productCutOff - The order part lead time to check. Generally, this matches
 *   the order part with max lead time.
 * @param weekDay - The day of the week the order will be delivered.
 * @param leadTimeConfiguration - The relevant lead time settings (inherited).
 */
const useCutoffTimeForWeekDay = (dropId?: number) => {
  const [ { drop } ] = useDrop(dropId);
  const [ { globalSettings } ] = useGlobalSettings();

  const cutOffTimeForWeekDay = useCallback(
    (leadTimeGroup: IProductLeadTimeGroup, weekDay: WeekDay) => {
      if (!drop) {
        return null;
      }

      const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

      if (!leadTimes || !defaultCutoff) {
        return null;
      }

      let leadTimeConfiguration = leadTimes.deliveryWeekLeadTimes;
      const deliveryDOWString = weekDay.toString();

      // The required lead time configuration is not found.
      if (!leadTimeConfiguration[ leadTimeGroup.name ] || !leadTimeConfiguration[ leadTimeGroup.name ][ deliveryDOWString ]) {
        // Complete the lead time configuration with defaults.
        const weekLeadTimes = completeLeadTimeConfiguration(
          globalSettings?.leadTimeGroups || [ leadTimeGroup ],
          { deliveryWeekLeadTimes: leadTimeConfiguration },
          defaultCutoff
        );

        leadTimeConfiguration = weekLeadTimes.deliveryWeekLeadTimes;
      }

      return leadTimeConfiguration[ leadTimeGroup.name ][ deliveryDOWString ].cutoffTime;
    },
    [ drop, globalSettings ]
  );

  return cutOffTimeForWeekDay;
};

export default useCutoffTimeForWeekDay;
